import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import FullScreenDialog from "../../components/Dialog/FullScreenDialog";
import PartnerClientDetails from "../../components/PartnerClientDetails/PartnerClientDetails";
import PartnerClientEditDetails from "../../components/PartnerClientDetails/PartnerClientEditDetails";
import { downloadKYCDocs } from "../../pages/Users/PartnersUsersDashboard";

interface SidebarProps {
    open: boolean;
    partner: any | null;
    onClose: () => void;
    fetchUsers: () => void;
}

export const getKycLabel = (val: any): string => {
    if (val === true || val === "true" || val === "verified") return "Verified";
    if (val === false || val === "false") return "Not Verified";
    return "Not Verified";
};

export const getKycStyle = (val: any) => {
    const label = getKycLabel(val).toLowerCase();
    if (label === "verified") return { bg: "#e3fcef", color: "#057a55" };
    return { bg: "#f0f0f0", color: "#666" };
};

export const sidebarStyles: Record<string, React.CSSProperties> = {
    overlay: {
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.25)",
        zIndex: 1200, transition: "opacity 0.25s",
    },
    drawer: {
        position: "fixed", top: 0, right: 0, height: "100vh", width: 460,
        background: "#fff", boxShadow: "-4px 0 24px rgba(0,0,0,0.12)",
        zIndex: 1201, display: "flex", flexDirection: "column",
        transition: "transform 0.3s cubic-bezier(0.4,0,0.2,1)",
        fontFamily: "'Segoe UI', sans-serif",
    },
    header: { padding: "20px 20px 0", borderBottom: "1px solid #f0f0f0" },
    headerTop: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 },
    partnerName: { fontSize: 18, fontWeight: 700, color: "#1a1a2e", margin: 0 },
    partnerMeta: { fontSize: 13, color: "#888", marginTop: 2 },
    closeBtn: { background: "none", border: "none", cursor: "pointer", color: "#888", padding: 4, borderRadius: 6, display: "flex", alignItems: "center" },
    filterArea: { padding: "14px 20px", borderBottom: "1px solid #f0f0f0", display: "flex", flexDirection: "column", gap: 10 },
    searchBox: { display: "flex", alignItems: "center", gap: 8, background: "#f5f6fa", borderRadius: 8, padding: "8px 12px", border: "1px solid #e8e8e8" },
    searchInput: { border: "none", background: "transparent", outline: "none", fontSize: 13, flex: 1, color: "#333" },
    filterRow: { display: "flex", gap: 8 },
    filterSelect: { flex: 1, fontSize: 13, padding: "6px 10px", border: "1px solid #e0e0e0", borderRadius: 8, background: "#f9f9f9", color: "#444", outline: "none", cursor: "pointer" },
    badge: { fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 20, display: "inline-block" },
    clientList: { flex: 1, overflowY: "auto", padding: "12px 20px" },
    clientCard: { display: "block", padding: "12px 14px", borderRadius: 10, border: "1px solid #f0f0f0", marginBottom: 10, background: "#fff", transition: "all 0.2s ease", },
    avatar: { width: 38, height: 38, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, flexShrink: 0 },
    clientInfo: { flex: 1, minWidth: 0 },
    clientName: { fontSize: 14, fontWeight: 600, color: "#1a1a2e", margin: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
    clientEmail: { fontSize: 12, color: "#888", margin: "2px 0 0", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
    statsRow: { display: "flex", gap: 8, padding: "0 0 14px" },
    statCard: { flex: 1, background: "#f5f6fa", borderRadius: 10, padding: "10px 12px", textAlign: "center" },
    statVal: { fontSize: 20, fontWeight: 700, color: "#1a1a2e" },
    statLbl: { fontSize: 11, color: "#888", marginTop: 2 },
    emptyState: { textAlign: "center", padding: "40px 20px", color: "#aaa", fontSize: 14 },
    footer: { padding: "12px 20px", borderTop: "1px solid #f0f0f0", fontSize: 12, color: "#aaa", textAlign: "center" },
};

export const PartnerSidebar: React.FC<SidebarProps> = ({ open, partner, onClose, fetchUsers }) => {
    const toastr = require("toastr");
    const history = useHistory();
    const [search, setSearch] = useState("");
    const [kycFilter, setKycFilter] = useState("all");
    const [deletedFilter, setDeletedFilter] = useState("all");
    const [sortBy, setSortBy] = useState("name");
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [selectedUserForView, setSelectedUserForView] = useState<any>(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedUserForEdit, setSelectedUserForEdit] = useState<any>(null);
    const editDetailsRef = React.useRef<any>(null);

    useEffect(() => {
        if (open) { setSearch(""); setKycFilter("all"); setDeletedFilter("all"); setSortBy("name"); }
    }, [open, partner?.uuid]);

    const clients: any[] = partner?.clients || [];

    const filtered = clients
        .filter((c) => {
            const client = c || {};
            const fullName = `${client.first_name || ""} ${client.last_name || ""}`.toLowerCase();
            const email = (client.email_id || "").toLowerCase();
            const mobile = (client.mobile_number || "").toLowerCase();
            const q = search.toLowerCase();
            if (q && !fullName.includes(q) && !email.includes(q) && !mobile.includes(q)) {
                return false;
            }
            if (kycFilter !== "all") {
                const kycValue = client.areKYCDetailsVerified;
                const label = getKycLabel(kycValue).toLowerCase();
                if (label !== kycFilter) return false;
            }
            if (deletedFilter === "active") {
                if (client.isDeleted === true || client.isDeleted === "true") return false;
            } else if (deletedFilter === "deleted") {
                if (client.isDeleted !== true && client.isDeleted !== "true") return false;
            }
            return true;
        })
        .sort((a, b) => {
            const clientA = a || {};
            const clientB = b || {};
            if (sortBy === "name") {
                const nameA = `${clientA.first_name || ""} ${clientA.last_name || ""}`.toLowerCase();
                const nameB = `${clientB.first_name || ""} ${clientB.last_name || ""}`.toLowerCase();
                return nameA.localeCompare(nameB);
            }
            if (sortBy === "date") {
                const dateA = clientA.inserted_at ? new Date(clientA.inserted_at).getTime() : 0;
                const dateB = clientB.inserted_at ? new Date(clientB.inserted_at).getTime() : 0;
                return dateB - dateA;
            }
            if (sortBy === "kyc") {
                const labelA = getKycLabel(clientA.areKYCDetailsVerified).toLowerCase();
                const labelB = getKycLabel(clientB.areKYCDetailsVerified).toLowerCase();
                return labelA.localeCompare(labelB);
            }
            return 0;
        });

    const verifiedCount = clients.filter((c) => getKycLabel(c.areKYCDetailsVerified).toLowerCase() === "verified").length;
    const activeCount = clients.filter((c) => !c.isDeleted).length;
    const handleEditUCC = (row: any) => {
        history.push(
            `/partner_client/update_ucc/${row.uuid}?user_type=client`
        );
    };

    if (!open) return null;

    return (
        <>
            <div style={sidebarStyles.overlay} onClick={onClose} />
            <div style={{ ...sidebarStyles.drawer, transform: open ? "translateX(0)" : "translateX(100%)" }}>

                <div style={sidebarStyles.header}>
                    <div style={sidebarStyles.headerTop}>
                        <div>
                            <p style={sidebarStyles.partnerName}>
                                {
                                    partner
                                        ? partner.company_name
                                            ? partner.company_name
                                            : `${partner.first_name || ""} ${partner.last_name || ""}`.trim()
                                        : "Partner"
                                }
                            </p>
                            <p style={sidebarStyles.partnerMeta}>
                                {partner.mobile_number}
                                {partner?.smest_id ? ` | SMEST ID : ${partner.smest_id}` : ""}
                            </p>
                        </div>
                        <button style={sidebarStyles.closeBtn} onClick={onClose}>
                            <CloseIcon fontSize="small" />
                        </button>
                    </div>

                    <div style={sidebarStyles.statsRow}>
                        <div style={sidebarStyles.statCard}>
                            <div style={sidebarStyles.statVal}>{clients.length}</div>
                            <div style={sidebarStyles.statLbl}>Total Clients</div>
                        </div>
                        <div style={sidebarStyles.statCard}>
                            <div style={{ ...sidebarStyles.statVal, color: "#057a55" }}>{verifiedCount}</div>
                            <div style={sidebarStyles.statLbl}>KYC Verified</div>
                        </div>
                        <div style={sidebarStyles.statCard}>
                            <div style={{ ...sidebarStyles.statVal, color: "#1a56db" }}>{activeCount}</div>
                            <div style={sidebarStyles.statLbl}>Active</div>
                        </div>
                    </div>
                </div>

                <div style={sidebarStyles.filterArea}>
                    <div style={sidebarStyles.searchBox}>
                        <SearchIcon style={{ fontSize: 16, color: "#999" }} />
                        <input
                            style={sidebarStyles.searchInput}
                            placeholder="Search by name, email, or mobile number"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <div style={sidebarStyles.filterRow}>
                        <select style={sidebarStyles.filterSelect} value={kycFilter} onChange={(e) => setKycFilter(e.target.value)}>
                            <option value="all">All KYC</option>
                            <option value="verified">Verified</option>
                            <option value="not verified">Not Verified</option>
                        </select>
                        <select style={sidebarStyles.filterSelect} value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                            <option value="name">Sort: Name</option>
                            <option value="date">Sort: Newest</option>
                            <option value="kyc">Sort: KYC</option>
                        </select>
                    </div>
                    <div style={{ fontSize: 12, color: "#aaa" }}>
                        Showing {filtered.length} of {clients.length} clients
                    </div>
                </div>

                <div style={sidebarStyles.clientList}>
                    {filtered.length === 0 ? (
                        <div style={sidebarStyles.emptyState}>
                            <PersonIcon style={{ fontSize: 40, color: "#ddd", marginBottom: 8 }} />
                            <div>{clients.length === 0 ? "No clients for this partner" : "No clients match your filters"}</div>
                        </div>
                    ) : (
                        filtered.map((client) => {
                            const kycStyle = getKycStyle(client.areKYCDetailsVerified);
                            return (
                                <div
                                    key={client.uuid}
                                    style={{
                                        ...sidebarStyles.clientCard,
                                        flexDirection: "column",
                                        alignItems: "stretch"
                                    }}
                                    onMouseEnter={(e) => (e.currentTarget.style.background = "#fafbff")}
                                    onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
                                >

                                    <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>

                                        <div style={sidebarStyles.clientInfo}>
                                            <p style={sidebarStyles.clientName}>
                                                {client.first_name} {client.last_name}
                                            </p>
                                            <p style={sidebarStyles.clientEmail}>
                                                {client.email_id || "—"}
                                            </p>
                                            {client.mobile_number && (
                                                <p style={{ ...sidebarStyles.clientEmail, marginTop: 1 }}>
                                                    {client.mobile_number}
                                                    {client?.smest_id ? ` | SMEST ID : ${client.smest_id}` : ""}
                                                </p>
                                            )}
                                        </div>

                                        <div style={{ textAlign: "right" }}>
                                            <span
                                                style={{
                                                    ...sidebarStyles.badge,
                                                    background: kycStyle.bg,
                                                    color: kycStyle.color,
                                                }}
                                            >
                                                {getKycLabel(client.areKYCDetailsVerified)}
                                            </span>

                                            {client.isDeleted && (
                                                <div
                                                    style={{
                                                        ...sidebarStyles.badge,
                                                        background: "#fee2e2",
                                                        color: "#b91c1c",
                                                        marginTop: 4,
                                                    }}
                                                >
                                                    Deleted
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div
                                        style={{
                                            marginTop: 12,
                                            width: "100%",
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            borderTop: "1px solid #f0f0f0",
                                            paddingTop: 10,
                                            gap: 10,
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: "flex",
                                                gap: 10,
                                                flexWrap: "wrap",
                                            }}
                                        >
                                            <div
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedUserForView(client);
                                                    setViewDialogOpen(true);
                                                }}
                                                style={{
                                                    border: "1px solid #dbeafe",
                                                    borderRadius: 8,
                                                    padding: "8px 12px",
                                                    cursor: "pointer",
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    minWidth: 75,
                                                    background: "#eff6ff",
                                                }}
                                            >
                                                <VisibilityIcon
                                                    style={{ fontSize: 20, color: "#2563eb" }}
                                                />
                                                <span
                                                    style={{
                                                        fontSize: 11,
                                                        marginTop: 4,
                                                        color: "#2563eb",
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    KYC View
                                                </span>
                                            </div>
                                            <div
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedUserForEdit(client);
                                                    setEditDialogOpen(true);
                                                }}
                                                style={{
                                                    border: "1px solid #e5e7eb",
                                                    borderRadius: 8,
                                                    padding: "8px 12px",
                                                    cursor: "pointer",
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    minWidth: 75,
                                                    background: "#f9fafb",
                                                }}
                                            >
                                                <EditIcon
                                                    style={{ fontSize: 20, color: "#4b5563" }}
                                                />
                                                <span
                                                    style={{
                                                        fontSize: 11,
                                                        marginTop: 4,
                                                        color: "#4b5563",
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    Edit
                                                </span>
                                            </div>
                                            <div
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    downloadKYCDocs(client, "client", toastr);
                                                }}
                                                style={{
                                                    border: "1px solid #e5e7eb",
                                                    borderRadius: 8,
                                                    padding: "8px 12px",
                                                    cursor: "pointer",
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    minWidth: 90,
                                                    background: "#f9fafb",
                                                }}
                                            >
                                                <DownloadIcon
                                                    style={{ fontSize: 20, color: "#4b5563" }}
                                                />
                                                <span
                                                    style={{
                                                        fontSize: 11,
                                                        marginTop: 4,
                                                        color: "#4b5563",
                                                        fontWeight: 600,
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    KYC Download
                                                </span>
                                            </div>
                                        </div>
                                        <div
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleEditUCC(client);
                                            }}
                                            style={{
                                                border: "1px solid #fecaca",
                                                borderRadius: 8,
                                                padding: "8px 12px",
                                                cursor: "pointer",
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                minWidth: 85,
                                                background: "#fef2f2",
                                            }}
                                        >
                                            <EditIcon
                                                style={{ fontSize: 20, color: "#dc2626" }}
                                            />
                                            <span
                                                style={{
                                                    fontSize: 11,
                                                    marginTop: 4,
                                                    color: "#dc2626",
                                                    fontWeight: 600,
                                                }}
                                            >
                                                Edit UCC
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
            <FullScreenDialog
                show={viewDialogOpen}
                heading={`${selectedUserForView?.first_name || ""} ${selectedUserForView?.last_name || ""}`}
                handleClose={() => setViewDialogOpen(false)}
                showSaveButton={true}
                saveButtonText="Save"
                onSumitSaveButton={() => {
                    setViewDialogOpen(false);
                    fetchUsers();
                }}
                inputProps={
                    <PartnerClientDetails
                        data={selectedUserForView}
                        user_type="client"
                    />
                }
            />
            <FullScreenDialog
                show={editDialogOpen}
                heading="Client Update Details"
                handleClose={() => setEditDialogOpen(false)}
                showSaveButton={true}
                saveButtonText="Save"
                onSumitSaveButton={async () => {
                    const success =
                        await editDetailsRef.current?.saveData();

                    if (success) {
                        setEditDialogOpen(false);
                        fetchUsers();
                    }
                }}
                inputProps={
                    <PartnerClientEditDetails
                        ref={editDetailsRef}
                        data={selectedUserForEdit}
                        user_type="client"
                    />
                }
            />
        </>
    );
};